import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Container,
  Divider,
  CircularProgress,
  Paper,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import moment from 'moment';
import { API, Storage, graphqlOperation } from 'aws-amplify';
import { v4 as uuidv4 } from 'uuid';
import { updateMission } from 'graphql/mutations';
import type Game from '../_types/Game';
import type IndividualGame from '../_types/IndividualGame';
import type ContestType from '../_types/ContestType';
import {
  EditIcon,
  CalendarIcon,
  GamifyIcon,
} from 'application/components/GamifyIcon';
import fetchKPIRules, { ProcessedKPIRule } from './fetchKPIRules';

const BannerImage = styled('img')({
  width: '100%',
  height: 'auto',
  minHeight: '180px',
  maxHeight: '250px',
  objectFit: 'cover',
  objectPosition: 'center',
});

const GameTitle = styled(Typography)(({ theme }) => ({
  marginTop: '16px',
  marginBottom: '4px',
  fontSize: '24px',
  fontWeight: 'bold',
  color: theme.custom.text,
}));

const GameSubtitle = styled(Typography)(({ theme }) => ({
  marginBottom: '8px',
  fontSize: '16px',
  color: theme.custom.text,
}));

const GameDescription = styled(Typography)(({ theme }) => ({
  color: theme.custom.textLight,
  marginBottom: '24px',
}));

const DateSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '16px 0',
  borderBottom: `1px solid ${theme.custom.stroke}`,
  width: '100%',
}));

const DateColumn = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
});

const DateDivider = styled(Box)(({ theme }) => ({
  width: '1px',
  height: '40px',
  backgroundColor: theme.custom.stroke,
  margin: '0 16px',
}));

const IconContainer = styled(Box)(({ theme }) => ({
  marginRight: '16px',
  color: theme.custom.text,
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  marginBottom: '4px',
  color: theme.custom.text,
}));

const RulesSection = styled(Box)(({ theme }) => ({
  padding: '16px 0',
  borderBottom: `1px solid ${theme.custom.stroke}`,
  display: 'flex',
  alignItems: 'flex-start',
}));

const GoalSection = styled(Box)(({ theme }) => ({
  padding: '16px 0',
  borderBottom: `1px solid ${theme.custom.stroke}`,
  display: 'flex',
  alignItems: 'flex-start',
}));

const ContentBox = styled(Box)({
  padding: '0 16px',
});

export interface Reward {
  id: string;
  name: string;
  description: string;
  imageName: string;
  place: number;
  points?: number;
  remainingCount?: number;
  purchaseCount?: number;
}

export interface Props {
  game: Game;
  rewards: Reward[];
}

interface Rule {
  id: string;
  name: string;
  goal: number;
  label: string;
  role: string;
  multiplier: number;
}

interface BracketRule {
  name: string;
  multiplier: number;
  role?: string;
  sequence?: number;
  goal?: number;
}

interface BracketRound {
  bracketName: string;
  missionRules: BracketRule[];
  KPIRuleName?: string;
  KPIRuleID?: string;
}

const GameRulesScreen: React.FC<Props> = ({ game }) => {
  const theme = useTheme();
  const [bannerImage, setBannerImage] = useState('');
  const [imageLoading, setImageLoading] = useState(true);
  const [uploadingPicture, setIsUploadingPicture] = useState(false);
  const [processedRules, setProcessedRules] = useState<Rule[]>([]);
  const [kpiRules, setKpiRules] = useState<ProcessedKPIRule[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Check if this is an incentive game
  const isIncentiveGame = (game as any).contestType === 'incentive';

  // Fetch KPI rules
  useEffect(() => {
    const getKpiRules = async () => {
      if (!window.me?.orgID) return;

      try {
        setIsLoading(true);
        const rules = await fetchKPIRules(window.me.orgID);
        setKpiRules(rules);
      } catch (error) {
        console.error('Error fetching KPI rules:', error);
      } finally {
        setIsLoading(false);
      }
    };

    getKpiRules();
  }, []);

  // Process game rules once we have KPI rules
  useEffect(() => {
    if (!game || kpiRules.length === 0) return;

    const processMissionRules = () => {
      const missionRules: Rule[] = [];
      const gameRules = (game as any).rules || [];

      if (gameRules.length > 0) {
        // Process each rule in the game
        gameRules.forEach((missionRule: Rule) => {
          // Find the matching KPI rule for additional details
          const matchedRule = kpiRules.find(
            (rule) => rule.value === missionRule.id
          );

          if (matchedRule) {
            // Combine the rule data from game and KPI data
            missionRules.push({
              ...missionRule,
              // Ensure these fields have fallback values
              label: missionRule.label || matchedRule.label,
              name: missionRule.name || matchedRule.label,
              multiplier: missionRule.multiplier || 1,
              role: missionRule.role || 'contestant',
            });
          } else {
            // Use only the game rule data if no KPI match is found
            missionRules.push({
              ...missionRule,
              label: missionRule.label || missionRule.name || 'Point',
              multiplier: missionRule.multiplier || 1,
              role: missionRule.role || 'contestant',
            });
          }
        });
      } else if ((game as IndividualGame).KPIRuleID) {
        // If no rules array but we have a KPIRuleID
        const matchedRule = kpiRules.find(
          (rule) => rule.value === (game as IndividualGame).KPIRuleID
        );

        if (matchedRule) {
          missionRules.push({
            id: matchedRule.value,
            name: matchedRule.label,
            label: matchedRule.label,
            multiplier: 1,
            role: 'contestant',
            goal: (game as any).actionCount || 0,
          });
        }
      }

      setProcessedRules(missionRules);
    };

    processMissionRules();
  }, [game, kpiRules]);

  useEffect(() => {
    async function fetchImage() {
      if (game?.imageName) {
        setImageLoading(true);
        try {
          const imageUrl = await Storage.get(game.imageName);
          if (imageUrl) {
            setBannerImage(
              typeof imageUrl === 'string' ? imageUrl : `${imageUrl}`
            );
          } else {
            setBannerImage('/images/game-rewards-placeholder.png');
          }
        } catch (err) {
          console.error('Error fetching image:', err);
          setBannerImage('/images/game-rewards-placeholder.png');
        } finally {
          setImageLoading(false);
        }
      } else {
        setBannerImage('/images/game-rewards-placeholder.png');
        setImageLoading(false);
      }
    }
    fetchImage();
  }, [game?.imageName]);

  const onPickImage = async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e: Event) => {
      const target = e.target as HTMLInputElement;
      const file = target.files?.[0];
      if (!file) return;

      setIsUploadingPicture(true);

      try {
        const extension = file.name.split('.').pop() || '';
        const name = `${uuidv4()}.${extension}`;

        await Storage.put(name, file, {
          contentType: file.type,
        });

        const missionInput = {
          id: game?.id,
          imageName: name,
          imageType: 'picture',
        };

        try {
          await API.graphql(
            graphqlOperation(updateMission, { input: missionInput })
          );
        } catch (error) {
          console.error('Error updating mission:', error);
        }

        setBannerImage(URL.createObjectURL(file));
        setIsUploadingPicture(false);
      } catch (err) {
        console.error('Upload failed:', err);
        setIsUploadingPicture(false);
      }
    };
    input.click();
  };

  if (uploadingPicture) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Get the contest type display name
  const getContestType = (type: ContestType) => {
    const contestTypes = {
      regular: 'All Play',
      percent_to_goal: 'Percentage to Goal',
      bracket_seed: 'Seed Matchup',
      bracket_manual: 'Bracket',
      copy: 'Copy',
      bracket_random: 'Random Bracket',
      bracket_kpi: 'KPI Bracket',
    };
    return contestTypes[type as keyof typeof contestTypes] || type;
  };

  const renderBanner = () => (
    <Box position="relative">
      {imageLoading ? (
        <Box
          sx={{
            width: '100%',
            height: '180px',
            minHeight: '180px',
            maxHeight: '250px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: theme.custom.background2,
          }}
        >
          <CircularProgress size={40} />
        </Box>
      ) : (
        <BannerImage
          src={bannerImage || '/images/game-rewards-placeholder.png'}
          alt={game.name}
          loading="eager"
          onLoad={() => setImageLoading(false)}
          onError={() => {
            setBannerImage('/images/game-rewards-placeholder.png');
            setImageLoading(false);
          }}
        />
      )}
      {window.me?.isAdmin && !imageLoading && (
        <IconButton
          onClick={onPickImage}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            backgroundColor: 'white',
            '&:hover': {
              backgroundColor: 'white',
            },
          }}
        >
          <EditIcon color="#323232" />
        </IconButton>
      )}
    </Box>
  );

  const renderGameInfo = () => (
    <ContentBox>
      <GameTitle variant="h5">{game.name}</GameTitle>
      <GameSubtitle variant="subtitle1">
        {getContestType(game.contestType as ContestType)}
      </GameSubtitle>
      <GameDescription variant="body2">{game.description}</GameDescription>
    </ContentBox>
  );

  const renderDateSection = () => (
    <DateSection>
      <IconContainer>
        <CalendarIcon width={24} height={24} color={theme.custom.text} />
      </IconContainer>
      <Box sx={{ display: 'flex', flex: 1, width: '100%' }}>
        <DateColumn>
          <SectionTitle variant="subtitle2">Start date:</SectionTitle>
          <Typography variant="body2" noWrap sx={{ color: theme.custom.text }}>
            {moment(game.startDate).format('MMM DD, YYYY hh:mm A')}
          </Typography>
        </DateColumn>
        <DateDivider />
        <DateColumn>
          <SectionTitle variant="subtitle2">End date:</SectionTitle>
          <Typography variant="body2" noWrap sx={{ color: theme.custom.text }}>
            {moment(game.endDate).format('MMM DD, YYYY hh:mm A')}
          </Typography>
        </DateColumn>
      </Box>
    </DateSection>
  );

  const renderBracketRules = () => {
    let roundValues: BracketRound[] = [];

    // Check if bracket exists using type assertion for IndividualGame
    const gameBracket = (game as any).bracket;

    if (gameBracket) {
      try {
        let parsedBracket;

        // Handle different bracket formats
        if (typeof gameBracket === 'string') {
          parsedBracket = JSON.parse(gameBracket);
        } else if (Array.isArray(gameBracket)) {
          parsedBracket = { roundValues: gameBracket };
        } else if (typeof gameBracket === 'object') {
          parsedBracket = gameBracket;
        }

        // Extract roundValues from the bracket object
        if (
          parsedBracket?.roundValues &&
          Array.isArray(parsedBracket.roundValues)
        ) {
          roundValues = parsedBracket.roundValues;
        }

        // Process each round to ensure it has valid missionRules
        roundValues.forEach((round) => {
          // Initialize missionRules if it doesn't exist or isn't an array
          if (!round.missionRules || !Array.isArray(round.missionRules)) {
            round.missionRules = [];
          }

          // If missionRules is empty, try to extract rule information
          if (round.missionRules.length === 0) {
            // Check for explicit KPIRuleName first
            if (round.KPIRuleName && round.KPIRuleName !== '') {
              round.missionRules.push({
                name: round.KPIRuleName,
                multiplier: 1,
              });
            }
            // If no explicit KPIRuleName but we have KPIRuleID, try to get rule from game rules
            else if (round.KPIRuleID && round.KPIRuleID !== '') {
              const gameRules = (game as any).rules || [];
              const matchingRule = gameRules.find(
                (rule: Rule) => rule?.id === round.KPIRuleID
              );

              if (matchingRule?.name) {
                round.missionRules.push({
                  name: matchingRule.name,
                  multiplier: matchingRule.multiplier || 1,
                });
              } else {
                // Use a generic "Points" rule if we can't find specific rule info
                round.missionRules.push({
                  name: 'Points',
                  multiplier: 1,
                });
              }
            } else {
              // Default to "Points" if no KPI rule information is available
              round.missionRules.push({
                name: 'Points',
                multiplier: 1,
              });
            }
          }
        });
      } catch (error) {
        console.error('Error processing bracket:', error);
      }
    }

    // If no rules are found, return null
    if (roundValues.length === 0) {
      return null;
    }

    return (
      <>
        {roundValues.map((round, index) => (
          <RulesSection key={`bracket-round-${index}`}>
            <IconContainer>
              <GamifyIcon icon="rules" color={theme.custom.text} />
            </IconContainer>
            <Box>
              <SectionTitle variant="subtitle2">
                Round: {round.bracketName}
              </SectionTitle>
              {round.missionRules?.map((rule, ruleIndex) => (
                <Typography
                  key={`rule-${index}-${ruleIndex}`}
                  variant="body2"
                  sx={{ color: theme.custom.text }}
                >
                  • Each {rule?.name || 'Point'}{' '}
                  {rule?.role ? `(${rule.role})` : ''} is{' '}
                  {rule?.multiplier || 1}{' '}
                  {(rule?.multiplier || 1) === 1 ? 'pt' : 'pts'}
                </Typography>
              ))}
            </Box>
          </RulesSection>
        ))}
      </>
    );
  };

  const renderRulesSection = () => {
    // Check if it's a bracket type game
    const isBracketGame =
      game.contestType === 'bracket_seed' ||
      game.contestType === 'bracket_manual' ||
      game.contestType === 'bracket_random' ||
      game.contestType === 'bracket_kpi';

    // If it's a bracket game, render bracket rules
    if (isBracketGame) {
      return renderBracketRules();
    }

    // If it's an incentive game, render incentive rules
    if (isIncentiveGame) {
      return (
        <RulesSection>
          <IconContainer>
            <GamifyIcon icon="rules" color={theme.custom.text} />
          </IconContainer>
          <Box>
            <SectionTitle variant="subtitle2">Rules</SectionTitle>
            {isLoading ? (
              <CircularProgress size={20} />
            ) : processedRules.length > 0 ? (
              <>
                <Typography
                  variant="body2"
                  sx={{ color: theme.custom.text, marginBottom: '8px' }}
                >
                  Complete {processedRules.length} task
                  {processedRules.length > 1 ? 's' : ''} to win.
                </Typography>
                {processedRules.map((rule: Rule, index: number) => (
                  <Typography
                    key={index}
                    variant="body2"
                    sx={{ color: theme.custom.text }}
                  >
                    • Get {rule.goal} {rule.name}
                  </Typography>
                ))}
              </>
            ) : (game as any).rules?.length === 0 ? (
              <Typography variant="body2" sx={{ color: theme.custom.text }}>
                • No rules defined
              </Typography>
            ) : null}
          </Box>
        </RulesSection>
      );
    }

    // For non-bracket, non-incentive games, render standard rules
    return (
      <RulesSection>
        <IconContainer>
          <GamifyIcon icon="rules" color={theme.custom.text} />
        </IconContainer>
        <Box>
          <SectionTitle variant="subtitle2">Rules</SectionTitle>
          {isLoading ? (
            <CircularProgress size={20} />
          ) : processedRules.length > 0 ? (
            processedRules.map((rule: Rule, index: number) => (
              <Typography
                key={index}
                variant="body2"
                sx={{ color: theme.custom.text }}
              >
                • Each {rule.label}{' '}
                {rule.role !== 'contestant' ? `(${rule.role})` : ''} is{' '}
                {rule.multiplier} {rule.multiplier === 1 ? 'pt' : 'pts'}
              </Typography>
            ))
          ) : (game as any).rules?.length === 0 &&
            (!('KPIRuleID' in game) || !(game as any).KPIRuleID) ? (
            <Typography variant="body2" sx={{ color: theme.custom.text }}>
              • No rules defined
            </Typography>
          ) : null}
        </Box>
      </RulesSection>
    );
  };

  const renderGoalSection = () => (
    <GoalSection>
      <IconContainer>
        <GamifyIcon icon="goal" color={theme.custom.text} />
      </IconContainer>
      <Box>
        <SectionTitle variant="subtitle2">Goal</SectionTitle>
        <Typography variant="body2" sx={{ color: theme.custom.text }}>
          {game.actionCount} Points
        </Typography>
      </Box>
    </GoalSection>
  );

  return (
    <Container
      disableGutters
      maxWidth="md"
      sx={{ bgcolor: theme.custom.background1, marginTop: '12px' }}
    >
      <Paper elevation={0} square sx={{ bgcolor: theme.custom.background1 }}>
        {renderBanner()}
        {renderGameInfo()}
        <Divider sx={{ bgcolor: theme.custom.stroke }} />
        <ContentBox>
          {renderDateSection()}
          {renderRulesSection()}
          {!isIncentiveGame && renderGoalSection()}
        </ContentBox>
      </Paper>
    </Container>
  );
};

export default GameRulesScreen;
